import { useAppDispatch } from "@/app/hooks";
import { mapboxToken } from "@/constants";
import { BAM_MAP_STYLE_SOURCES } from "@/constants/mapStyles";
import { selectMarketMapKeys } from "@/features/survey/surveySelectors";
import { updateToggleLabelMapKey } from "@/features/survey/surveySlice";
import { useFetchGeocodeAddressMutation } from "@/services/survey-core";
import { useUpdateSurveyMapKeyMutation } from "@/services/survey-mapKeys";
import {
  checkValueIsUnavailable,
  getActiveFieldLabelList,
  hasAddressLine2AsCustom,
  hasAddressLine2Field,
  isEmptyObjectFromStr,
} from "@/shared/utils";
import * as mapUtil from "@/shared/utils/map";
import { EmeraldCheckbox } from "@emerald-react/checkbox";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldInputV2 } from "@emerald-react/input-v2";
import { usePropertyFieldUpdate } from "@hooks/usePropertyFieldUpdate";
import { useSurvey } from "@hooks/useSurveySelectors";
import { useUpdatePropertyMutation } from "@services/survey-properties";
import { Property } from "@survey-types/survey.property.types";
import { updateProperty as updatePropertyAction } from "@survey/surveySlice";
import classNames from "classnames";
import debounce from "lodash/debounce";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { DetailedSurvey } from "../../types/survey.core.types";
import MapOverlay from "./MapOverlay";

interface PropertyDetailAddressFormProps {
  property: Property;
  propertyId: string;
}

const PropertyDetailAddressForm = ({
  property,
  propertyId,
}: PropertyDetailAddressFormProps) => {
  const dispatch = useAppDispatch();
  const currentSurvey = useSurvey();
  const propertyFieldRule =
    currentSurvey?.surveyPropertyFieldRule?.propertyFieldRuleList;
  const activeFieldRuleList = getActiveFieldLabelList(propertyFieldRule || []);
  const { latitude, longitude, bamMapData } = property || {};
  const [fetchGeocodeAddress] = useFetchGeocodeAddressMutation();
  const mapRef = useRef<any>(null);
  const mapAddressContainerRef = useRef<any>(null);
  const isMapInitialized = useRef<boolean>(false);
  const defaultBamValues = {
    mapCentroid: mapUtil.isValidCoordinates([
      parseFloat(longitude ?? ""),
      parseFloat(latitude ?? ""),
    ])
      ? [parseFloat(longitude ?? ""), parseFloat(latitude ?? "")]
      : [-95, 40],
    mapZoomLevel: 12,
  };
  const [addressDetails, setAddressDetails] = useState(property);
  const [updateProperty] = useUpdatePropertyMutation();
  const [updateSurveyMapKey] = useUpdateSurveyMapKeyMutation();
  const mapKeys = useSelector(selectMarketMapKeys);
  const isAddressLine2Custom = hasAddressLine2AsCustom(
    currentSurvey as DetailedSurvey,
  );
  const isAddressLine2Field = hasAddressLine2Field(
    currentSurvey as DetailedSurvey,
  );

  // Helper function to normalize dash values to empty string
  const normalizeValue = (value: string | undefined) => {
    return value === "—" ? "" : value;
  };
  const { updateFieldRule } = usePropertyFieldUpdate();
  const [coordinates, setCoordinates] = useState<[any, any]>([
    longitude,
    latitude,
  ]);
  const [propertyMapData, setPropertyMapData] = useState<any>(() => {
    if (
      bamMapData &&
      typeof JSON.parse(bamMapData) === "object" &&
      !isEmptyObjectFromStr(bamMapData)
    ) {
      return JSON.parse(bamMapData);
    }
    return defaultBamValues;
  });
  const [isLatInvalid, setIsLatInvalid] = useState(false);
  const [isLngInvalid, setIsLngInvalid] = useState(false);

  const isMapReady = () => {
    if (!mapRef.current || !isMapInitialized.current) return false;
    try {
      return Boolean(mapRef.current.getContainer());
    } catch {
      return false;
    }
  };

  const fetchCoords = (payload: any) => {
    return fetchGeocodeAddress(JSON.stringify(payload))
      .then((response) => {
        return response?.data?.features[0]?.geometry?.coordinates;
      })
      .catch((_error) => {
        console.error("Error fetching coordinates");
        return null;
      });
  };

  useEffect(() => {
    // Guard: ensure container ref is available before initializing map
    if (!mapAddressContainerRef.current) {
      return;
    }

    mapboxgl.accessToken = mapboxToken || "";
    mapRef.current = new mapboxgl.Map({
      container: mapAddressContainerRef.current,
      style: BAM_MAP_STYLE_SOURCES.cbreLight,
      center: propertyMapData.mapCentroid || [-95, 40],
      zoom: propertyMapData.mapZoomLevel || 2.5,
      attributionControl: false,
    });

    // Mark map as initialized BEFORE calling render functions
    isMapInitialized.current = true;

    const points = mapUtil.getPointsForAddressForm(coordinates, property || {});
    if (mapUtil.isValidCoordinates(coordinates)) {
      mapUtil.getMarkerConfig(points, mapRef.current);
      updateMapView(coordinates, defaultBamValues.mapZoomLevel);
    } else {
      updateMapView([-95, 40], 2.5);
    }
    const map = mapRef.current;
    mapEvent();
    mapRef.current?.resize();

    return () => {
      isMapInitialized.current = false;
      map.off("zoomend");
      map.off("dragend");
      mapRef.current.remove();
    };
  }, []);

  // Reset state when property changes (property toggle)
  useEffect(() => {
    if (property) {
      // Reset address details to current property
      setAddressDetails(property);

      // Reset coordinates
      const newCoordinates: [any, any] = [property.longitude, property.latitude];
      setCoordinates(newCoordinates);

      // Reset map data
      const newMapData =
        property.bamMapData &&
        typeof JSON.parse(property.bamMapData) === "object" &&
        !isEmptyObjectFromStr(property.bamMapData)
          ? JSON.parse(property.bamMapData)
          : defaultBamValues;
      setPropertyMapData(newMapData);

      // Re-render map with new property data (only if map is initialized)
      if (isMapReady()) {
        renderMapPoints(newCoordinates, property);
      }

      // Reset validation states
      const lat = parseFloat(property.latitude);
      const lng = parseFloat(property.longitude);
      const latInvalid =
        isNaN(lat) ||
        lat < -90 ||
        lat > 90 ||
        property.latitude.trim() === "" ||
        lat === 0;
      const lngInvalid =
        isNaN(lng) ||
        lng < -180 ||
        lng > 180 ||
        property.longitude.trim() === "" ||
        lng === 0;
      setIsLatInvalid(latInvalid);
      setIsLngInvalid(lngInvalid);
    }
  }, [propertyId, property?.latitude, property?.longitude, property?.bamMapData, property?.isCurrentLocation]);

  const mapEvent = () => {
    const map = mapRef.current;
    const handleZoom = (e: { originalEvent: any }) => {
      if (e && !e.originalEvent) return;
      const data = { ...propertyMapData, mapZoomLevel: map.getZoom() };
      setPropertyMapData(data);
      updateMapData(data);
    };

    const handleCentroid = () => {
      const center = map.getCenter();
      const data = {
        ...propertyMapData,
        mapCentroid: [center.lng, center.lat],
      };
      setPropertyMapData(data);
      updateMapData(data);
    };

    const { _markers: markers } = mapRef.current;
    if (markers?.length > 0) {
      markers.forEach((marker: mapboxgl.Marker) => {
        const handleDragEnd = () => {
          const geoDetail = marker.getLngLat();
          const { lng, lat } = geoDetail;
          if (lng && lat) {
            setCoordinates([lng, lat]);
            dispatch(
              updatePropertyAction({
                propertyId: propertyId,
                updates: {
                  latitude: `${lat}`,
                  longitude: `${lng}`,
                },
              }),
            );
            updateProperty({
              propertyId: propertyId,
              updates: {
                latitude: `${lat}`,
                longitude: `${lng}`,
              },
            });
          }
        };

        marker.on("dragend", handleDragEnd);
      });
    }

    map.on("zoomend", debounce(handleZoom));
    map.on("dragend", debounce(handleCentroid));
  };

  const renderMapPoints = (
    coordinatesParam: [any, any],
    propertyParam: Property,
  ) => {
    if (!isMapReady()) return;

    mapUtil.deleteMarkers(mapRef.current);
    const points = mapUtil.getPointsForAddressForm(
      [coordinatesParam[0], coordinatesParam[1]],
      propertyParam || {},
    );
    if (mapUtil.isValidCoordinates(coordinatesParam)) {
      mapUtil.getMarkerConfig(points, mapRef.current);
      updateMapView(coordinatesParam, 12);
      mapEvent();
    } else {
      updateMapView([-95, 40], 2.5);
    }
  };

  const updateMapView = (newCenter: [number, number], newZoom: number) => {
    const map = mapRef.current;
    map.setCenter(newCenter);
    map.setZoom(newZoom || 2.5);
  };

  const updateMapData = (data: any) => {
    if (data && Object.keys(data).length > 0) {
      dispatch(
        updatePropertyAction({
          propertyId: propertyId,
          updates: {
            bamMapData: JSON.stringify(data),
          },
        }),
      );
      updateProperty({
        propertyId: propertyId,
        updates: {
          bamMapData: JSON.stringify(data),
        },
      });
    }
  };

  const updateAddress = (
    value: boolean | string,
    field: string,
    fieldLabel?: string,
  ) => {
    // If the user focused a "—" field without entering a value, normalizeValue
    // turned the display into "" but the stored value is still "—". Skip the
    // PATCH so we don't overwrite "—" with an empty string unintentionally.
    if (
      typeof value === "string" &&
      value.trim() === "" &&
      property?.[field as keyof Property] === "\u2014"
    ) {
      return;
    }

    // For boolean fields like isCurrentLocation, always send the value (true or false)
    // For string fields, only update if different from current value
    const shouldUpdate =
      typeof value === "boolean" ||
      value !== property?.[field as keyof Property];

    if (shouldUpdate) {
      dispatch(
        updatePropertyAction({
          propertyId: propertyId,
          updates: { [field]: value },
        }),
      );
      updateProperty({
        propertyId: propertyId,
        updates: { [field]: value },
      });

      // Update field rule if value is a non-empty string - use label if provided, otherwise field key
      if (typeof value === "string" && value.trim()) {
        updateFieldRule(fieldLabel || field, value);
      }
    }
  };

  const updateCoordinates = (value: string, field: string) => {
    const updatedValue = String(value);

    // Validate the coordinate value in real-time
    const numValue = parseFloat(updatedValue);

    if (field === "latitude") {
      // Validate latitude (treat 0 as invalid)
      const latInvalid =
        updatedValue.trim() === "" ||
        isNaN(numValue) ||
        numValue < -90 ||
        numValue > 90 ||
        numValue === 0;
      setIsLatInvalid(latInvalid);

      setCoordinates([coordinates[0], updatedValue]);
      setAddressDetails(
        (prevDetails) =>
          ({
            ...prevDetails,
            [field]: value,
          }) as Property,
      );
      if (property) {
        renderMapPoints([coordinates[0], updatedValue], property);
      }
    } else {
      // Validate longitude (treat 0 as invalid)
      const lngInvalid =
        updatedValue.trim() === "" ||
        isNaN(numValue) ||
        numValue < -180 ||
        numValue > 180 ||
        numValue === 0;
      setIsLngInvalid(lngInvalid);

      setCoordinates([updatedValue, coordinates[1]]);
      setAddressDetails(
        (prevDetails) =>
          ({
            ...(prevDetails as Property),
            [field]: value,
          }) as Property,
      );
      if (property) {
        renderMapPoints([updatedValue, coordinates[1]], property);
      }
    }
  };

  const isValidGeoAddress = (addressDetails: Property) => {
    const { address1, city, stateProvince, postalCode, latitude, longitude } =
      addressDetails || {};
    if (
      (!Number(latitude) || !Number(longitude)) &&
      address1.length > 1 &&
      ((city.length > 1 && stateProvince.length > 1) ||
        (postalCode && postalCode.length >= 5))
    ) {
      const payload = {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            properties: {
              Address: address1,
              City: city,
              StateProv: stateProvince,
            },
          },
        ],
      };
      fetchCoords(payload)
        .then((coords) => {
          const areCoordinatesValid =
            !!coords?.length && mapUtil.isValidCoordinates(coords);
          if (areCoordinatesValid && mapRef.current) {
            const [longitude, latitude] = coords;
            dispatch(
              updatePropertyAction({
                propertyId: propertyId,
                updates: {
                  latitude: `${latitude}`,
                  longitude: `${longitude}`,
                },
              }),
            );
            return updateProperty({
              propertyId: propertyId,
              updates: { latitude: `${latitude}`, longitude: `${longitude}` },
            }).then(() => {
              setCoordinates(coords);
              setAddressDetails(
                (prevDetails) =>
                  ({
                    ...prevDetails,
                    latitude: `${latitude}`,
                    longitude: `${longitude}`,
                  }) as Property,
              );
              mapUtil.deleteMarkers(mapRef.current);
              const updatedPoints = mapUtil.getPointsForAddressForm(
                coords,
                addressDetails || {},
              );
              mapUtil.getMarkerConfig(updatedPoints, mapRef.current);
              updateMapView(coords, 12);
              mapEvent();
            });
          } else {
            console.error("Unable to fetch coordinates.");
            updateMapView([-95, 40], 2.5);
          }
        })
        .catch((_error) => {
          console.error("Error fetching coordinates");
        });
    }
  };

  const debouncedIsValidGeoAddress = useMemo(() => {
    // use Memo is needed here
    return debounce((updatedAddressDetails) => {
      isValidGeoAddress(updatedAddressDetails);
    }, 500);
  }, []);

  // Check if current property has invalid coordinates
  const hasInvalidCoordinates = useMemo(() => {
    if (!property) {
      setIsLatInvalid(false);
      setIsLngInvalid(false);
      return false;
    }

    const lat = parseFloat(property.latitude);
    const lng = parseFloat(property.longitude);

    const latInvalid =
      isNaN(lat) ||
      lat < -90 ||
      lat > 90 ||
      property.latitude.trim() === "" ||
      lat === 0;
    const lngInvalid =
      isNaN(lng) ||
      lng < -180 ||
      lng > 180 ||
      property.longitude.trim() === "" ||
      lng === 0;

    setIsLatInvalid(latInvalid);
    setIsLngInvalid(lngInvalid);

    return latInvalid || lngInvalid;
  }, [property?.latitude, property?.longitude]);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | boolean,
    field: string,
  ) => {
    if (typeof event === "boolean") {
      if (event !== addressDetails[field as keyof Property]) {
        const allProperties = currentSurvey?.properties || [];
        const iconColor = mapUtil.getIconData(property).iconColor.toLowerCase();
        const previousIsCurrentLocation =
          addressDetails[field as keyof Property];

        const updatedMapKey = mapUtil.resolveMapKeyLocationUpdate(
          allProperties,
          propertyId,
          iconColor,
          (previousIsCurrentLocation ?? false) as boolean,
          (event ?? false) as boolean,
          mapKeys ?? [],
        );
        if (updatedMapKey) {
          updateSurveyMapKey({
            mapKeyId: updatedMapKey.mapKeyId!,
            mapKey: updatedMapKey,
            surveyId: currentSurvey?.id || "",
          });
          dispatch(updateToggleLabelMapKey(updatedMapKey));
        }
      }
      setAddressDetails(
        (prevDetails) =>
          ({
            ...prevDetails,
            [field]: event,
          }) as Property,
      );
      updateAddress(event, field);
      property &&
        renderMapPoints(coordinates, {
          ...property,
          isCurrentLocation: event,
        });
    } else {
      const { value } = event.target;
      setAddressDetails(
        (prevDetails) =>
          ({
            ...prevDetails,
            [field]: value,
          }) as Property,
      );
      // updateAddress(value, field);
      debouncedIsValidGeoAddress({
        ...addressDetails,
        [field]: value,
      } as Property);
    }
  };

  return (
    <>
      {hasInvalidCoordinates && (
        <div
          style={{
            backgroundColor: "#FFF2E5",
            borderColor: "#D5710C",
            marginTop: "-18px",
          }}
          className="px-4 py-3 border rounded-sm flex items-center gap-2"
        >
          <EmeraldIcon
            icon="warning_amber"
            type="filled"
            fontsizeInRem={1.2}
            className="text-orange-600"
            variant="icon"
          />
          <span
            className="text-warning-dark"
            style={{ fontSize: "0.875rem", fontWeight: 100 }}
          >
            Invalid or missing coordinates data. Please update this property
            with valid coordinates.
          </span>
        </div>
      )}
      <div className="address-mapOverview-container grid grid-flow-row lg:grid-cols-2 gap-6 sm:grid-cols-1">
        <div className="Property-detail-address-form">
          <h2 className="mb-3 text-xl text-data">Address</h2>
          <EmeraldCheckbox
            label="Current Location"
            checked={addressDetails?.isCurrentLocation}
            onChange={(value) => handleInputChange(value, "isCurrentLocation")}
          />
          <div className="my-4">
            <EmeraldInputV2
              type="text"
              label={"Street Address"}
              value={normalizeValue(property?.address1)}
              size="small"
              className={classNames("without-label", {
                boldLabel: activeFieldRuleList?.includes("Address"),
              })}
              isValid={true}
              onBlur={(e) => {
                updateAddress(e.target.value, "address1", "Address");
              }}
              onChange={(e) => handleInputChange(e, "address1")}
            />
          </div>
          {isAddressLine2Field && !isAddressLine2Custom && (
            <div className="my-4">
              <EmeraldInputV2
                type="text"
                label={"Address Line 2"}
                value={normalizeValue(property?.address2)}
                size="small"
                className={classNames("without-label", {
                  boldLabel: activeFieldRuleList?.includes("Address Line 2"),
                })}
                onBlur={(e) => {
                  updateAddress(e.target.value, "address2", "Address Line 2");
                }}
                onChange={(e) => handleInputChange(e, "address2")}
              />
            </div>
          )}
          <div className="gap-3 my-5 grid grid-flow-row lg:grid-cols-3 sm:grid-cols-1">
            <EmeraldInputV2
              type="text"
              label={"City"}
              value={normalizeValue(property?.city)}
              size="small"
              className={classNames("without-label", {
                boldLabel: activeFieldRuleList?.includes("City"),
              })}
              onBlur={(e) => {
                updateAddress(e.target.value, "city", "City");
              }}
              onChange={(e) => {
                handleInputChange(e, "city");
              }}
            />
            <EmeraldInputV2
              type="text"
              label={"State"}
              value={normalizeValue(property?.stateProvince)}
              size="small"
              className={classNames("without-label", {
                boldLabel: activeFieldRuleList?.includes("State Province"),
              })}
              onBlur={(e) => {
                updateAddress(e.target.value, "stateProvince", "State");
              }}
              onChange={(e) => {
                handleInputChange(e, "stateProvince");
              }}
            />
            <EmeraldInputV2
              type="text"
              label={"Zip Code"}
              value={normalizeValue(property?.postalCode)}
              size="small"
              className={classNames("without-label", {
                boldLabel: activeFieldRuleList?.includes("Postal Code"),
              })}
              onBlur={(e) => {
                updateAddress(e.target.value, "postalCode", "Zip Code");
              }}
              onChange={(e) => {
                handleInputChange(e, "postalCode");
              }}
            />
          </div>
          <div className="gap-3 my-5 grid grid-flow-row lg:grid-cols-2 sm:grid-cols-1">
            <EmeraldInputV2
              type="number"
              id="SC-SurveyPropertyLat"
              label={"Latitude"}
              value={coordinates?.[1] || ""}
              size="small"
              errorText={isLatInvalid ? "Invalid Latitude" : undefined}
              className={classNames("without-label", {
                boldLabel: activeFieldRuleList?.includes("Latitude"),
              })}
              onChange={(e) => {
                updateCoordinates(e.target.value, "latitude");
              }}
              onBlur={(e) => {
                updateAddress(e.target.value, "latitude", "Latitude");
              }}
            />
            <EmeraldInputV2
              type="number"
              id="SC-SurveyPropertyLong"
              label={"Longitude"}
              value={coordinates?.[0] || ""}
              size="small"
              errorText={isLngInvalid ? "Invalid Longitude" : undefined}
              className={classNames("without-label", {
                boldLabel: activeFieldRuleList?.includes("Longitude"),
              })}
              onChange={(e) => {
                updateCoordinates(e.target.value, "longitude");
              }}
              onBlur={(e) => {
                updateAddress(e.target.value, "longitude", "Longitude");
              }}
            />
          </div>
        </div>
        <div className="map-overview relative">
          <div
            id="map-address-container"
            ref={mapAddressContainerRef}
            className="h-full w-full relative"
          >
            {checkValueIsUnavailable(coordinates?.[0]) &&
              checkValueIsUnavailable(coordinates?.[1]) && <MapOverlay />}
          </div>
        </div>
      </div>
    </>
  );
};

export default PropertyDetailAddressForm;
